import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tick02Icon, ArrowLeft01Icon, PlusSignIcon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { log } from '@/lib/logger';
import { INVALID_INVITE_EMAIL_MESSAGE, isValidInviteEmail } from '@/lib/inviteEmail';

/**
 * Rows the step offers at most. The approved 2026-07-05 spec's cap: a Team
 * trial has 5 seats and the owner holds one, so 4 is the most a fresh team can
 * invite; larger plans keep the same cap so the step stays one screen.
 */
export const MAX_INVITE_ROWS = 4;

/** What the wizard knows about the team's seats, read from /api/billing/plan. */
export interface InviteSeatInfo {
  planName: string;
  maxSeats: number;
  /** True while the plan comes from the beta trial (billing.betaExpiresAt set). */
  isTrial: boolean;
  /** maxSeats minus the seats already taken; 0 means this step is not shown at all. */
  invitableSeats: number;
}

/** The step-3 goal whose wording this step echoes ("You said you want to collaborate"). */
export const COLLABORATION_GOAL = 'Collaborating with my team';

interface StepInviteTeamProps {
  /** Controlled rows; the wizard keeps at least one so the form is never empty. */
  emails: string[];
  onChangeEmails: (emails: string[]) => void;
  /** The step-1 company name, or the wizard's own fallback ('My Company'). */
  teamName: string;
  goal?: string;
  /** Null when billing never loaded: the seat sentence is then omitted, never guessed. */
  seats?: InviteSeatInfo | null;
  /** Local site: the pending copy says "Setting up your project..." like StepProject. */
  isLocalEntry?: boolean;
  /** Finish: the wizard saves, invites the typed addresses, routes. Only after the gate below. */
  onFinish: () => void;
  /** Skip for now: the wizard saves and routes; nothing typed is sent. */
  onSkip: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

/**
 * Onboarding step 5: "Who reviews with you?"
 *
 * Design: docs/get-started-pin-step-proposal.html (cards
 * wizard-step-invite-empty / -filled), copy from its table. The step collects
 * addresses and validates on the Finish click, answering the click with the
 * Settings form's own sentence rather than a silently disabled button (the
 * StepProject gate shape). It sends nothing itself: sending happens in the
 * wizard AFTER the save, so an invite can never go out for a team the save
 * did not create. Skip for now and an empty Finish are the same outcome for
 * the user; the step only reports which control was pressed.
 */
export function StepInviteTeam({
  emails,
  onChangeEmails,
  teamName,
  goal,
  seats = null,
  isLocalEntry = false,
  onFinish,
  onSkip,
  onBack,
  isSubmitting,
}: StepInviteTeamProps) {
  // Set on a Finish click that found a malformed row; cleared as the user edits.
  const [invalidIndex, setInvalidIndex] = useState<number | null>(null);
  const rowCap = Math.max(1, Math.min(MAX_INVITE_ROWS, seats ? seats.invitableSeats : MAX_INVITE_ROWS));
  const canAddRow = emails.length < rowCap;
  const hasTypedEmail = emails.some((e) => e.trim() !== '');
  const collaboration = goal === COLLABORATION_GOAL;
  log.info('[StepInviteTeam] render', { rows: emails.length, rowCap, hasTypedEmail, isSubmitting });

  function updateRow(index: number, value: string) {
    if (invalidIndex === index) setInvalidIndex(null);
    onChangeEmails(emails.map((e, i) => (i === index ? value : e)));
  }

  function removeRow(index: number) {
    log.info('[StepInviteTeam] row removed', { index });
    setInvalidIndex(null);
    onChangeEmails(emails.filter((_, i) => i !== index));
  }

  function addRow() {
    if (!canAddRow) return;
    log.info('[StepInviteTeam] row added', { rows: emails.length + 1 });
    onChangeEmails([...emails, '']);
  }

  function handleFinish() {
    const bad = emails.findIndex((e) => e.trim() !== '' && !isValidInviteEmail(e));
    if (bad !== -1) {
      log.info('[StepInviteTeam] finish blocked: malformed address', { index: bad });
      setInvalidIndex(bad);
      return;
    }
    log.info('[StepInviteTeam] finish clicked', { typed: emails.filter((e) => e.trim() !== '').length });
    onFinish();
  }

  // Only a real, finite count is worth a sentence: no billing, or an unlimited
  // plan, and the helper stops after the team name rather than printing a guess.
  const seatSentence = seats && Number.isFinite(seats.maxSeats)
    ? ` Your ${seats.planName} ${seats.isTrial ? 'trial' : 'plan'} includes ${seats.maxSeats} seats.`
    : '';
  // A company name that already ends in punctuation ("Acme Inc.") closes the
  // sentence itself; adding our own period printed "Acme Inc.." (caught by the
  // copy test before it shipped).
  const joinSentence = `When you finish, we'll email each of them a magic link to join ${teamName}${/[.!?]$/.test(teamName) ? '' : '.'}`;
  const pendingLabel = hasTypedEmail
    ? 'Sending invites and opening your review...'
    : isLocalEntry
      ? 'Setting up your project...'
      : 'Opening your review...';

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="hero-title">{collaboration ? "Who's on your team?" : 'Who reviews with you?'}</h2>
        <p className="mt-2 text-gray-500">
          {collaboration
            ? "You said you want to collaborate. They'll see and comment on the issues you capture."
            : "They'll see and comment on the issues you capture."}
        </p>
      </div>

      <div className="space-y-2">
        <span id="onboarding-invite-label" className="field-label">
          Teammate emails <span className="text-gray-400 font-normal">(optional)</span>
        </span>
        <div className="space-y-2" role="group" aria-labelledby="onboarding-invite-label">
          {emails.map((email, index) => {
            const isInvalid = invalidIndex === index;
            return (
              <div key={index} className="flex items-center gap-2">
                <Input
                  id={`onboarding-invite-email-${index}`}
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => updateRow(index, e.target.value)}
                  placeholder="colleague@example.com"
                  autoFocus={index === 0}
                  disabled={isSubmitting}
                  error={isInvalid}
                  aria-invalid={isInvalid}
                  aria-label={`Teammate email ${index + 1}`}
                  aria-describedby={isInvalid ? 'onboarding-invite-error' : undefined}
                />
                {emails.length > 1 && (
                  <button
                    type="button"
                    aria-label="Remove teammate"
                    className="shrink-0 rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => removeRow(index)}
                    disabled={isSubmitting}
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {invalidIndex !== null && (
          <p id="onboarding-invite-error" className="text-sm text-red-600" role="alert">
            {INVALID_INVITE_EMAIL_MESSAGE}
          </p>
        )}
        {canAddRow && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={addRow}
            disabled={isSubmitting}
          >
            <HugeiconsIcon icon={PlusSignIcon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
            <span className="text-cap-trim">Add another</span>
          </button>
        )}
        {/* Names the send moment and the team from step 1, so the earlier ask pays off here. */}
        <p className="text-xs text-gray-500">{joinSentence}{seatSentence}</p>
      </div>

      <Button
        variant="primary"
        size="md"
        className="w-full"
        disabled={isSubmitting}
        onClick={handleFinish}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            {pendingLabel}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            Finish
            <HugeiconsIcon icon={Tick02Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
          </span>
        )}
      </Button>

      {/* Two ghost controls on one row: the wizard's Back pattern extended, not a second primary. */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="ghost"
          size="md"
          disabled={isSubmitting}
          onClick={() => {
            log.info('[StepInviteTeam] back clicked');
            onBack();
          }}
        >
          <span className="flex items-center gap-2">
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={2} color="currentColor" className="h-4 w-4" />
            Back
          </span>
        </Button>
        <Button
          variant="ghost"
          size="md"
          disabled={isSubmitting}
          onClick={() => {
            log.info('[StepInviteTeam] skip clicked', { typed: emails.filter((e) => e.trim() !== '').length });
            onSkip();
          }}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
}
